import { useEffect, useRef, useState } from 'react'
import { apiGet } from '../../Api_Functions'
import { useAuthorization } from '../useAuthorization'
import { alarmCountsBySection, onAlarmsMarked } from '../../../maintenance/alarms&notifications/alarmSectionsApi'

// S130 (D16, P6, R-A): the counter is per user and queried live on the server — the in-memory cache
// that used to serve it is gone — and every shop-floor MOS terminal is an open session polling this.
// Story 94 moved it from 1 s to 15 s for that reason. Story 162 moves it back to 1 s by the PO's decision
// (25/09): an alarm has to show on the button as it happens, and 15 s read as "the alarms do not update".
// It stays a single, explicit value; the cost of the query is the backend's to watch.
export const ALARM_COUNTER_REFRESH_MS = 1000

// ONE poll per session, however many places show the count. The top bar has it always, and the alarms
// view adds its tabs: with a poll each, a session asked twice a second (Judge, story 162, P2-2). Everyone
// who shows the count joins this poll and gets the same answer; the poll runs while at least one is shown.
const pollListeners = new Set()
let pollFetcher = async () => ({})
let pollTimer = null
// Restarting the poll (a reading, or everybody leaving) starts a new generation, so a fetch of the old one
// that was still on its way does not schedule a second poll next to the new one.
let pollGeneration = 0
let lastSummary = {}

// A failed fetch keeps the counts as they were; the next one tries again.
const fetchAndShare = async () => {
  try {
    const summary = (await pollFetcher()) || {}
    lastSummary = summary
    pollListeners.forEach((listener) => listener(summary))
  } catch (error) {}
}

// The next fetch is asked for one interval after the previous one came back, so a slow answer never piles
// requests up behind it.
const scheduleNextFetch = () => {
  const generation = pollGeneration
  pollTimer = setTimeout(async () => {
    await fetchAndShare()
    if (generation === pollGeneration && pollListeners.size > 0) scheduleNextFetch()
  }, ALARM_COUNTER_REFRESH_MS)
}

const stopPoll = () => {
  pollGeneration += 1
  clearTimeout(pollTimer)
  pollTimer = null
}

// Reading alarms asks for the count once, right away, for everybody showing it, and the poll starts
// counting again from there.
const refreshNow = () => {
  if (pollListeners.size === 0) return
  stopPoll()
  fetchAndShare()
  scheduleNextFetch()
}

const joinPoll = (fetcher, listener) => {
  pollFetcher = fetcher
  pollListeners.add(listener)
  if (pollListeners.size === 1) {
    fetchAndShare()
    scheduleNextFetch()
  } else {
    listener(lastSummary)
  }
  return () => {
    pollListeners.delete(listener)
    if (pollListeners.size === 0) {
      stopPoll()
      lastSummary = {}
    }
  }
}

onAlarmsMarked(refreshNow)

const useUncheckedAlarms = () => {
  const [uncheckedNotifications, setUncheckedNotifications] = useState(0)
  const [uncheckedAlarms, setUncheckedAlarms] = useState(0)
  // Story 162: the same summary, split into the two sections of the alarms screen.
  const [alarmsBySection, setAlarmsBySection] = useState({ general: 0, automation: 0 })
  const isUserAuthorized = useAuthorization()

  const fetchData = async () => {
    // S127 (RN32, D17): each icon's counter depends on ITS OWN permission. Gating the whole refresh
    // on `ALARMS` also silenced the bell, so a supervisor with `NOTIFICATIONS` and without `ALARMS`
    // saw no counter at all — and the job start/finish notices are INF, i.e. bell.
    if (!isUserAuthorized('ALARMS') && !isUserAuthorized('NOTIFICATIONS')) {
      return {}
    }

    let data
    await apiGet('getUncheckedAlarmSummary').then((value) => {
      data = value
    })
    return data
  }
  // The poll always asks with the permissions of the latest render, like the interval it replaced did.
  const fetchDataRef = useRef(fetchData)
  fetchDataRef.current = fetchData

  useEffect(() => joinPoll(() => fetchDataRef.current(), (summary) => {
    setUncheckedAlarms(summary.alarms && Number.isInteger(summary.alarms) ? summary.alarms : 0)
    setUncheckedNotifications(summary.notifications && Number.isInteger(summary.notifications) ? summary.notifications : 0)
    setAlarmsBySection(alarmCountsBySection(summary))
  }), [])

  return { uncheckedNotifications, uncheckedAlarms, alarmsBySection }
}

export default useUncheckedAlarms
